import {
  IconAWS,
  IconGCP,
  IconAzure,
  IconKubernetes,
  IconContainer,
  IconServer04,
  IconOracle,
  IconVultr,
  IconUsaFlag,
  IconGlobe4,
} from 'components/Icons';
import { IconGPU } from 'components/Icons/IconGPU';
import { ComputeClusters } from 'api/computeClusters/types';
import { clustersApiEffects } from 'api/computeClusters/apiEffects';
import { CreateNodepoolBody, DeploymentsRequestBody } from 'api/nodepools/types';
import { nodepoolsApiEffects } from 'api/nodepools/apiEffects';
import { IconCPUFill } from 'components/Icons/IconCPUFill';
import { ComputeSelectOptionType, ComputeProviderDisplayComponent, ComputeRegionDisplayComponent, DeploymentFormValues } from 'modules/Compute/types';
import { computeProviderStyles, computeHardwareTemplateStyles } from 'modules/Compute/styles';
import { Tooltip } from 'components/PlatformResourceSelector/Tooltip';
import { IconBracketsDots } from 'components/Icons/IconBracketsDots';
import { copyToClipboard } from 'utils/copyToClipboard';

const CloudProviderInfo = ({ icon, text }: ComputeProviderDisplayComponent) => {
  return (
    <div className={computeProviderStyles.optionLayout}>
      {icon && <div className={computeProviderStyles.optionIcon}>{icon}</div>}
      <div className={computeProviderStyles.optionText}>{text}</div>
    </div>
  );
};

const getRegionIcon = (region: string) => {
  const normalizedRegion = region?.toLowerCase();

  for (const key in REGION_ICON_MAP) {
    if (normalizedRegion?.includes(key)) {
      return REGION_ICON_MAP[key];
    }
  }
  // Fallback icon...
  return <IconUsaFlag />;
};

/**
 * Get the formatted region ID for a given region ID, varies by use case.
 * Ensures only one rule is applied per condition.
 * @param regionId - the region ID to format
 * @returns the formatted region ID
 */
const getFormattedRegionId = (regionId: string): string => {
  // Define an array of rules as objects
  const formattingRules = [
    {
      condition: (id: string) => id === 'na',
      transform: (_id: string) => 'N/A',
    },
  ];

  // Apply the first matching rule
  for (const rule of formattingRules) {
    if (rule.condition(regionId)) {
      return rule.transform(regionId);
    }
  }

  // Default return if no rules match
  return regionId;
};

const CloudRegionInfo = ({ icon, text }: ComputeRegionDisplayComponent) => {
  return (
    <div className={computeProviderStyles.optionLayout}>
      <div className={computeProviderStyles.optionIcon}>{icon}</div>
      <div className={computeProviderStyles.optionText}>{text}</div>
    </div>
  );
};

export const ComputeSelectOption = ({ icon, text }: { icon?: JSX.Element; text: string }) => {
  return (
    <div className={computeProviderStyles.optionLayout}>
      {icon && <div className={computeProviderStyles.optionIcon}>{icon}</div>}
      <div className={computeProviderStyles.optionText}>{text}</div>
    </div>
  );
};

/**
 * Get the region info for a given region ID
 * @param regionId - the region ID to get info for
 * @returns the region info, including the display component
 */
export const getComputeRegionInfo = (regionId: string) => {
  const regionIcon = getRegionIcon(regionId);
  const formattedRegionId = getFormattedRegionId(regionId);

  return {
    name: regionId,
    component: <CloudRegionInfo icon={regionIcon} text={formattedRegionId} />,
  };
};

export const getComputeRegionInfoAll = (regionId?: string) => {
  const _regionId = regionId || COMPUTE_PROVIDER_INFO_MAP.all.id;
  const { component } = getComputeRegionInfo(_regionId);
  return {
    value: _regionId,
    name: component,
    content: component,
  };
};

export const getDefaultAllSelectOption = ({ text }: { text: string }) => {
  return {
    value: 'all',
    name: <ComputeSelectOption text={text} />,
    content: <ComputeSelectOption text={text} />,
  };
};

export const REGION_ICON_MAP: { [key: string]: JSX.Element } = {
  us: <IconUsaFlag size={20} />,
  na: <IconGlobe4 size={18} />,
};

export const RAW_PROVIDER_ICON_MAP: { [key: string]: React.ElementType } = {
  aws: IconAWS,
  gcp: IconGCP,
  azure: IconAzure,
  kubernetes: IconKubernetes,
  local: IconServer04,
  oracle: IconOracle,
  vultr: IconVultr,
};

export const PROVIDER_ICON_MAP: { [key: string]: JSX.Element } = {
  aws: <IconAWS size={22} />,
  gcp: <IconGCP size={22} />,
  azure: <IconAzure size={22} />,
  kubernetes: <IconKubernetes size={20} />,
  oracle: <IconOracle size={20} />,
  vultr: <IconVultr size={20} />,
  local: <IconServer04 size={20} />,
};

export const COMPUTE_PROVIDER_INFO_MAP: { [key: string]: ComputeSelectOptionType } = {
  all: {
    id: 'all',
    name: 'All Providers',
    handle: 'All Providers',
  },
  aws: {
    id: 'aws',
    icon: PROVIDER_ICON_MAP.aws,
    rawIcon: RAW_PROVIDER_ICON_MAP.aws,
    name: 'Amazon Web Services',
    handle: 'AWS',
  },
  gcp: {
    id: 'gcp',
    icon: PROVIDER_ICON_MAP.gcp,
    rawIcon: RAW_PROVIDER_ICON_MAP.gcp,
    name: 'Google Cloud Platform',
    handle: 'GCP',
  },
  azure: {
    id: 'azure',
    icon: PROVIDER_ICON_MAP.azure,
    rawIcon: RAW_PROVIDER_ICON_MAP.azure,
    name: 'Microsoft Azure',
    handle: 'Azure',
  },
  kubernetes: {
    id: 'kubernetes',
    icon: PROVIDER_ICON_MAP.kubernetes,
    rawIcon: RAW_PROVIDER_ICON_MAP.kubernetes,
    name: 'Kubernetes',
    handle: 'K8s',
  },
  local: {
    id: 'local',
    icon: PROVIDER_ICON_MAP.local,
    rawIcon: RAW_PROVIDER_ICON_MAP.local,
    name: 'Local Cluster',
    handle: 'Local',
  },
  oracle: {
    id: 'oracle',
    icon: PROVIDER_ICON_MAP.oracle,
    rawIcon: RAW_PROVIDER_ICON_MAP.oracle,
    name: 'Oracle Distributed Cloud',
    handle: 'Oracle',
  },
  vultr: {
    id: 'vultr',
    icon: PROVIDER_ICON_MAP.vultr,
    rawIcon: RAW_PROVIDER_ICON_MAP.vultr,
    name: 'Vultr Cloud Servers',
    handle: 'Vultr',
  },
};

export const HARDWARE_INFO_MAP: { [key: string]: ComputeSelectOptionType } = {
  all: {
    id: 'all',
    name: 'All Hardware',
    handle: 'All Hardware',
  },
  cpu: {
    id: 'cpu',
    name: 'CPU',
    handle: 'CPU',
    icon: <IconCPUFill size={20} />,
  },
  gpu: {
    id: 'gpu',
    name: 'GPU',
    handle: 'GPU',
    icon: <IconGPU size={20} />,
  },
};

/**
 * Get the provider info for a given provider ID
 * @param providerId - the provider ID to get info for
 * @param useShortName - whether to use the short name for the provider
 * @returns the provider info, including the display component
 */
export const getComputeProviderInfo = (providerId: string, useShortName?: boolean) => {
  const providerDetails = COMPUTE_PROVIDER_INFO_MAP[providerId];

  // If we don't have a provider mapping, return a default fallback:
  if (!providerDetails) {
    return {
      name: providerId,
      icon: <></>,
      component: <CloudProviderInfo icon={<IconContainer size={20} color="#667085" />} text={providerId} />,
    };
  }

  // We can optionally use a shorter display name for tight spaces:
  const providerText = useShortName ? providerDetails.handle : providerDetails.name;
  const providerComponent = <CloudProviderInfo icon={providerDetails.icon || <></>} text={providerText} />;

  // Otherwise, return the provider details with the generated display component:
  return {
    ...providerDetails,
    component: providerComponent,
  };
};

// Generates a 4-character alphanumeric string using a cryptographically secure PRNG.
// Not used for authentication—just helps avoid name collisions in auto-generated labels.
// Replaces Math.random() to satisfy static analysis (CWE-338).
export const createFourDigitRandomString = (): string => {
  const charset = 'abcdefghijklmnopqrstuvwxyz0123456789';
  const array = crypto.getRandomValues(new Uint8Array(4));
  return Array.from(array, (byte) => charset[byte % charset.length]).join('');
};

/**
 * Create a cluster ID
 * @param provider - the provider
 * @param region - the region
 * @param firstName - the first name
 * @param lastName - the last name
 * @returns the created cluster ID
 * if user is Sagar Bajpai, take sb, then prodvider, followed by region, then 4 digit random string
 */
export const createClusterID = (provider: string, region: string, firstName: string, lastName?: string) => {
  try {
    const firstChars = lastName ? `${firstName.charAt(0)}${lastName.charAt(0)}` : firstName.charAt(0);
    const clusterId = `${firstChars}-${provider}-${region}-${createFourDigitRandomString()}`;
    // Only alphanumeric characters, hyphens, and underscores are allowed
    return clusterId.toLowerCase().replace(/[^\w-]/g, '');
  } catch (error) {
    console.error('Error creating cluster ID:', error);
    return '';
  }
};

/**
 * Create a nodepool ID
 * @param instanceType - the instance type
 * @param cpuLimit - the cpuLimit
 * @returns the created nodepool ID
 */
export const createNodepoolID = (instanceType: string, cpuLimit: string) => {
  if (!instanceType || !cpuLimit) return '';
  const cpuCores = getCpuCoresFromLimit(cpuLimit);
  // Only small letter alphanumeric characters, hyphens, and underscores are allowed
  return `${instanceType}-${cpuCores}-${createFourDigitRandomString()}`.toLowerCase().replace(/[^\w-]/g, '');
};

export function initialValues({
  isNodepool = false,
  clusterId = '',
  computeCluster,
  isEditMode = false,
  existingNodepool = null,
}: {
  isNodepool?: boolean;
  clusterId?: string;
  computeCluster: CF.API.Compute.ComputeCluster | null;
  isEditMode?: boolean;
  existingNodepool?: CF.API.Compute.NodePool | null;
}) {
  const cloudProviderId = computeCluster?.cloud_provider?.id || COMPUTE_PROVIDER_INFO_MAP.all.id;
  const regionId = computeCluster?.region || '';
  const { name: providerName, component: providerComponent } = getComputeProviderInfo(cloudProviderId);

  return {
    type: 'cluster' as const,
    cloudProvider: {
      value: cloudProviderId,
      name: providerName,
      content: providerComponent,
    },
    region: {
      value: regionId,
      name: regionId || 'All Regions',
      content: regionId ? getComputeRegionInfo(regionId).component : undefined,
    },
    clusterId,
    clusterDescription: '',
    personalAccessToken: { value: '', name: '' },
    isSaving: false,
    isEditMode,
    computeCluster: computeCluster || ({} as CF.API.Compute.ComputeCluster),
    nodepools: [],
    instanceTypes: [],
    nodepoolId: existingNodepool?.id || '',
    nodepoolDescription: existingNodepool?.description || '',
    accelerator: '',
    instanceType: existingNodepool?.instance_types?.[0] || ({} as CF.API.Compute.InstanceType),
    minInstances: existingNodepool?.min_instances || 0,
    maxInstances: existingNodepool?.max_instances || 2,
    spotInstances: existingNodepool?.node_capacity_type?.capacity_types?.includes(1) || false,
    searchQuery: '',
    hardware: {
      value: '',
      name: '',
    },
    instanceTypeFilter: {
      value: '',
      name: '',
      content: <ComputeSelectOption text="" />,
    },
    isNodepool,
  };
}

/**
 * Creates a compute cluster with the given parameters
 * @param params - Parameters for creating the compute cluster
 * @returns Promise<boolean> - Whether the cluster creation was successful
 */
export const createComputeCluster = async ({
  userId,
  clusterId,
  clusterDescription,
  instanceType,
  personalAccessToken,
}: {
  userId: string;
  clusterId: string;
  clusterDescription: string;
  instanceType: CF.API.Compute.InstanceType;
  personalAccessToken: { value: string };
}) => {
  const isLocal = instanceType?.cloud_provider?.id === 'local';
  const computeCluster = {
    id: clusterId,
    description: clusterDescription,
    region: isLocal ? 'local' : instanceType.region,
    cloud_provider: instanceType?.cloud_provider,
    user_id: userId,
    cluster_type: isLocal ? 'local-dev' : 'dedicated',
    managed_by: isLocal ? 'user' : 'clarifai',
    key: {
      id: personalAccessToken.value,
    },
    visibility: {
      gettable: 10,
    },
  };

  const computeClusterBody: ComputeClusters = {
    compute_clusters: [computeCluster],
  };

  return clustersApiEffects.createCluster(userId, computeClusterBody);
};

/**
 * Creates a nodepool with the given parameters
 * @param params - Parameters for creating the nodepool
 * @returns Promise<boolean> - Whether the nodepool creation was successful
 */
export const createNodepool = async ({
  userId,
  clusterId,
  nodepoolId,
  nodepoolDescription,
  instanceType,
  minInstances,
  maxInstances,
  spotInstances,
}: {
  userId: string;
  clusterId: string;
  nodepoolId: string;
  nodepoolDescription: string;
  instanceType: CF.API.Compute.InstanceType;
  minInstances: number;
  maxInstances: number;
  spotInstances: boolean;
}) => {
  const createDeploymentBody: CreateNodepoolBody = {
    nodepools: [
      {
        id: nodepoolId,
        description: nodepoolDescription,
        node_capacity_type: {
          capacity_types: [spotInstances ? 2 : 1],
        },
        instance_types: [instanceType],
        min_instances: minInstances,
        max_instances: maxInstances,
        visibility: {
          gettable: 10,
        },
      },
    ],
  };

  return nodepoolsApiEffects.createNodepool(userId, clusterId, createDeploymentBody);
};

/**
 * Get the request body for deploying a model
 * @param isEditing - whether the model is being edited
 * @param userId - the user ID
 * @param values - the form values
 * @returns the request body
 */
export const getDeployModelRequestBody = ({
  isEditing,
  userId,
  values,
}: {
  isEditing: boolean;
  userId: string;
  values: DeploymentFormValues;
}): DeploymentsRequestBody => {
  const {
    clusterId,
    deploymentDescription,
    deploymentId,
    disablePacking = false,
    deployLatestVersion = true,
    maxReplicas,
    minReplicas,
    model,
    modelVersion,
    modelVisibility,
    nodePoolId,
    scaleDownDelay,
    scaleToZeroDelay,
    scaleUpDelay,
    scalingTimeframe,
  } = values;

  let requestBody: DeploymentsRequestBody = {
    deployments: [
      {
        id: deploymentId,
        description: deploymentDescription || '',
        user_id: userId,
        autoscale_config: {
          min_replicas: minReplicas ? Number.parseInt(minReplicas, 10) : undefined,
          max_replicas: maxReplicas ? Number.parseInt(maxReplicas, 10) : undefined,
          traffic_history_seconds: Number.parseInt(scalingTimeframe, 10),
          scale_down_delay_seconds: Number.parseInt(scaleDownDelay, 10),
          scale_up_delay_seconds: Number.parseInt(scaleUpDelay, 10),
          scale_to_zero_delay_seconds: Number.parseInt(scaleToZeroDelay, 10),
          disable_packing: disablePacking,
        },
        deploy_latest_version: deployLatestVersion,
        worker: {
          model: {
            id: model?.id || '',
            ...(modelVersion?.value ? { model_version: { id: modelVersion.value } } : {}),
            user_id: model?.user_id || '',
            app_id: model?.app_id || '',
          },
        },
        scheduling_choice: 4, // performance
        nodepools: [
          {
            id: nodePoolId.value,
            compute_cluster: { id: clusterId.value, user_id: userId },
          },
        ],
        visibility: {
          gettable: modelVisibility ?? 10,
        },
      },
    ],
  };

  if (isEditing) {
    requestBody = {
      ...requestBody,
      action: 'overwrite',
    };
    delete requestBody.deployments[0].nodepools;
  }

  return requestBody;
};

// PAT-related constants and functions
export const REQUIRED_COMPUTE_SCOPES = [
  'ComputeClusters:Add',
  'ComputeClusters:Delete',
  'ComputeClusters:Get',
  'Deployments:Add',
  'Deployments:Delete',
  'Deployments:Get',
  'InstanceTypes:Get',
  'Keys:Get',
  'Models:Get',
  'Nodepools:Add',
  'Nodepools:Delete',
  'Nodepools:Get',
  'Runners:Add',
  'Runners:Delete',
  'Runners:Get',
  'Workflows:Get',
];

export const checkIfPatHasComputeAccess = (pat: CF.API.ApiKeys.KeyData) => {
  // Only global PATs can be used for compute access:
  if (pat?.type === 'app_specific_key') return false;

  // Full Access:
  if (pat?.scopes?.includes('All')) return true;

  // Check compute required scopes here:
  if (REQUIRED_COMPUTE_SCOPES.every((scope) => pat?.scopes?.includes(scope))) return true;

  // If no compute access, return false:
  return false;
};

// Documentation and text constants
export const computeDocumentation = {
  quickstart: 'https://docs.clarifai.com/getting-started/quickstart/',
  computeOverview: 'https://docs.clarifai.com/compute/overview/',
  setUp: 'https://docs.clarifai.com/portal-guide/compute-orchestration/set-up-compute/',
  createNodepool: 'https://docs.clarifai.com/portal-guide/compute-orchestration/set-up-compute/#how-to-create-a-nodepool',
  instanceTypes: 'https://docs.clarifai.com/portal-guide/compute-orchestration/cloud-instances/',
  localRunners: 'https://docs.clarifai.com/compute/models/upload/run-locally/',
  localRunnersQuickstart: '/compute/local-runners-quickstart',
};

export const computeLinks = {
  contactUs: 'https://www.clarifai.com/explore/contact-us',
  marketingPage: 'https://www.clarifai.com/products/compute-orchestration',
};

export const NODEPOOL_ID_TEXT = `The ID helps identify which nodepool to use when deploying models. We recommend
an easy to remember ID that's related to the model's use case.`;

export const NODEPOOL_DESCRIPTION_TEXT = `It will help you to identify this node pool when you deploy models. We recommend
to use a description that summarize the details related to this deployment.`;

export const NODEPOOL_AUTOSCALING_RANGE_TEXT =
  'Minimum is the number of nodes to autoscale down to when there is no Nodepool usage. Maximum is the highest number of nodes your deployments will autoscale up to. Scaling down to 1 instead of 0 avoids cold start time after inactivity. This may be important if you have specific latency requirements, but means a node will always be running and incurring compute costs.';

export const SPOT_INSTANCE_TOOLTIP =
  'This nodepool will only use spot instances. Spot instances are cheaper, but less reliable as they may be rotated or unavailable.';

export const SPOT_INSTANCES_TEXT =
  'If selected, this nodepool will only use spot instances if supported by the selected cloud provider. Spot instances are cheaper, but less reliable as they may be rotated or unavailable.';

export const COST_PER_HOUR_TOOLTIP = 'Cost (per hour) in USD for each node instance deployed.';

export const DEVELOPER_PLAN_PRICE = 1;

export const LOCAL_RUNNERS_QUICKSTART_TEXT = 'Run your own models locally for development, testing, and production. Get started in minutes.';

export const LOCAL_RUNNERS_VALUE_PROP_TEXT =
  'Run powerful AI models directly on your machine. Ultra-low latency, complete privacy, and no data ever leaves your device.';

// Region-related constants
export const COMPUTE_PROVIDER_REGION_MAP: { [key: string]: string[] } = {
  aws: ['us-east-1', 'us-west-1'],
  gcp: ['us-central1', 'us-east1', 'us-east4'],
  azure: ['eastus', 'westus'],
  local: ['na'],
};

// Helper functions
export const getFormattedDate = (dateStr: string) => {
  const date = new Date(dateStr);
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };
  const formattedDate = date.toLocaleDateString('en-US', options);
  return formattedDate;
};

export const getMemoryInGi = (memory: string | undefined, count?: number) => {
  // If there's no memory, return a dash
  if (!memory) return '-';

  // If there's no count, default to 1
  if (!count) count = 1;

  // Memory strings come in the format of "8Gi" or "8192Mi"
  const memoryNumber = Number(memory.replace('Gi', '').replace('Mi', ''));
  const totalMemory = memoryNumber * count;

  // If the memory is already in Gi, just return it
  if (memory.includes('Gi')) {
    // If there's only one gpu, pass the value right through
    if (count === 1) {
      return memoryNumber % 1 === 0
        ? memoryNumber.toLocaleString(undefined)
        : Number(memoryNumber).toLocaleString(undefined, { maximumFractionDigits: 2 });
    }
    // We have multiple gpus, so we need to calculate the total memory
    return totalMemory % 1 === 0
      ? totalMemory.toLocaleString(undefined)
      : Number(totalMemory).toLocaleString(undefined, { maximumFractionDigits: 2 });
  } else {
    // We're working with Mi, so we need to convert it to Gi
    const giValue = totalMemory / 1024;
    return giValue % 1 === 0 ? giValue.toLocaleString(undefined) : Number(giValue).toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
};

export const getCpuCoresFromLimit = (cpuLimit?: string) => {
  if (!cpuLimit) return '0';

  const cpuLimitNumber = Number(cpuLimit.replace('m', ''));
  if (cpuLimitNumber < 0) return 0;
  const cpuCores = (cpuLimitNumber / 1000).toFixed(1);
  return cpuCores;
};

// Instance GPU Template
export const makeModelGpuRequirementsTemplate = (computeInfo?: CF.API.Models.InferenceComputeInfo) => {
  // Extract GPU details
  const acceleratorTypes = computeInfo?.accelerator_type;
  const acceleratorCount = computeInfo?.num_accelerators ? `${computeInfo.num_accelerators}x` : '';
  const gpuMemory = getMemoryInGi(computeInfo?.accelerator_memory);

  return (
    <div className={computeHardwareTemplateStyles.instanceItem}>
      <IconGPU />
      {acceleratorTypes?.length ? (
        <div className={computeHardwareTemplateStyles.templateContent}>
          {acceleratorCount} <span>{acceleratorTypes.join(', ')}</span> <span className={computeHardwareTemplateStyles.memory}>({gpuMemory}Gi)</span>
        </div>
      ) : (
        <span>-</span>
      )}
    </div>
  );
};

export const makeModelCpuRequirementsTemplate = (computeInfo?: CF.API.Models.InferenceComputeInfo) => {
  const rawCpuLimit = computeInfo?.cpu_limit;
  const rawCpuMemory = computeInfo?.cpu_memory;
  const cpuMemory = getMemoryInGi(computeInfo?.cpu_memory);

  return (
    <div className={computeHardwareTemplateStyles.instanceItem}>
      <IconCPUFill />
      {rawCpuLimit || '-'}
      <span className={computeHardwareTemplateStyles.memory}>{rawCpuMemory ? `(${cpuMemory}Gi)` : ''}</span>
    </div>
  );
};

// Instance Config Template
export const makeModelConfigRequirementsTemplate = (computeInfo?: CF.API.Models.InferenceComputeInfo) => {
  const configDetailsData = Object.keys(computeInfo || {}).length === 0 ? 'No configuration' : JSON.stringify(computeInfo, null, 2);
  const configDetailsTemplate = (
    <div>
      <p className={computeHardwareTemplateStyles.configDetailsTitle}>Model Configuration Details</p>
      <pre className={computeHardwareTemplateStyles.configDetails}>{configDetailsData}</pre>
    </div>
  );

  // On click, copy the config details to the clipboard
  const handleCopyConfig = () => {
    copyToClipboard(configDetailsData, 'Model config copied to clipboard.')();
  };

  return (
    <div className={computeHardwareTemplateStyles.instanceItem}>
      <Tooltip text={configDetailsTemplate}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <IconBracketsDots />
          <button type="button" aria-label="Copy model config" onClick={handleCopyConfig} className={computeHardwareTemplateStyles.configDetailsLink}>
            config.yaml
          </button>
        </div>
      </Tooltip>
    </div>
  );
};

// Instance GPU Template
export const makeGpuTemplate = ({
  instance,
  showMemory = true,
  fullMemory = false,
}: {
  instance?: CF.API.Compute.InstanceType['compute_info'];
  showMemory?: boolean;
  fullMemory?: boolean;
}) => {
  // Extract GPU details
  const acceleratorType = instance?.accelerator_type;
  const acceleratorCount = instance?.num_accelerators ? `${instance.num_accelerators}x` : '';
  const gpuMemory = getMemoryInGi(instance?.accelerator_memory, fullMemory ? instance?.num_accelerators : 1);

  return (
    <div className={computeHardwareTemplateStyles.instanceItem} style={{ opacity: !acceleratorType ? 0.5 : 1 }}>
      <IconGPU />
      {acceleratorType ? (
        <span>
          {acceleratorCount} {acceleratorType} {showMemory && <span className={computeHardwareTemplateStyles.memory}>({gpuMemory}Gi)</span>}
        </span>
      ) : (
        <span>-</span>
      )}
    </div>
  );
};

// Instance CPU Template
export const makeCpuTemplate = ({
  instance,
  showMemory = true,
}: {
  instance?: CF.API.Compute.InstanceType['compute_info'];
  showMemory?: boolean;
}) => {
  // Extract CPU details
  const cpuLimit = instance?.cpu_limit;
  const cpuMemory = getMemoryInGi(instance?.cpu_memory);
  const cpuCores = getCpuCoresFromLimit(cpuLimit);

  return (
    <div className={computeHardwareTemplateStyles.instanceItem} style={{ opacity: !cpuLimit ? 0.5 : 1 }}>
      <IconCPUFill />
      {cpuLimit && Number(cpuCores) > 0 ? (
        <span>
          {cpuCores} cores {showMemory && <span className={computeHardwareTemplateStyles.memory}>({cpuMemory}Gi)</span>}
        </span>
      ) : (
        <span>-</span>
      )}
    </div>
  );
};

// Full Instance Hardware
export const makeHardwareTemplate = ({
  instance,
  showMemory = true,
  fullMemory = false,
}: {
  instance?: CF.API.Compute.InstanceType['compute_info'];
  showMemory?: boolean;
  fullMemory?: boolean;
}) => {
  return (
    <div className={computeHardwareTemplateStyles.layout}>
      {makeCpuTemplate({ instance, showMemory })}
      {makeGpuTemplate({ instance, showMemory, fullMemory })}
    </div>
  );
};

/**
 * Get provider and region data for display
 * @param providerId - the provider ID
 * @param regionId - the region ID
 * @param useShortName - whether to use the short name for the provider
 * @returns [providerData, regionData] - array containing provider and region display data
 */
export const getProviderData = (providerId: string, regionId: string, useShortName?: boolean) => {
  const providerData = getComputeProviderInfo(providerId, useShortName);
  const regionData = getComputeRegionInfo(regionId);
  return [providerData, regionData];
};

export interface DateFormatOptions {
  month?: 'numeric' | '2-digit' | 'long' | 'short' | 'narrow';
  day?: 'numeric' | '2-digit';
  year?: 'numeric' | '2-digit';
  hour?: 'numeric' | '2-digit';
  minute?: 'numeric' | '2-digit';
  second?: 'numeric' | '2-digit';
}

/**
 * Formats a date string into a human-readable format.
 * @param dateString - ISO date string
 * @param formatOptions - Optional date format options
 * @returns Formatted date string (e.g., "Mar 14, 2025")
 */
export const formatDate = (dateString: string, formatOptions?: DateFormatOptions): string => {
  if (!dateString) return '-';

  const defaultOptions: DateFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  };

  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', formatOptions || defaultOptions);
};

/**
 * Generates a URL for deploying a new model
 * @param params - Parameters for generating the URL
 * @returns URL for deploying a new model
 */
export const generateDeployNewModelUrl = ({
  appId,
  clusterId,
  modelId,
  modelVersionId,
  nodePoolId,
  userId,
}: {
  appId?: string;
  clusterId?: string;
  modelId?: string;
  modelVersionId?: string;
  nodePoolId?: string;
  userId?: string;
}) => {
  const query = [
    appId && `appId=${encodeURIComponent(appId)}`,
    clusterId && `computeClusterId=${encodeURIComponent(clusterId)}`,
    modelId && `modelId=${encodeURIComponent(modelId)}`,
    modelVersionId && `modelVersionId=${encodeURIComponent(modelVersionId)}`,
    nodePoolId && `nodePoolId=${encodeURIComponent(nodePoolId)}`,
    userId && `userId=${encodeURIComponent(userId)}`,
  ]
    .filter(Boolean)
    .join('&'); // Join with '&'

  const modelDeploymentUrl = '/compute/deployments/create';

  return query ? `${modelDeploymentUrl}?${query}` : modelDeploymentUrl;
};

export const parseNumberToString = (value: number | undefined): string => (value === undefined || value === null ? '' : String(value));

/**
 * Utility helpers to check whether a model's inference compute requirements
 * are satisfiable by a node-pool's instance type.
 */
export interface CompatibilityResult {
  compatible: boolean;
  reasons?: string[];
}

/**
 * Parse a Kubernetes-style CPU string (e.g. "2500m" or "2") into core count.
 */
export const parseCpuCores = (cpuLimit?: string | null): number => {
  if (!cpuLimit) return 0;
  try {
    if (cpuLimit.endsWith('m')) {
      return Number(cpuLimit.slice(0, -1)) / 1000;
    }
    return Number(cpuLimit);
  } catch {
    return 0;
  }
};

/**
 * Convert a memory string such as "23028Mi" or "18Gi" into MiB.
 */
export const parseMemoryMi = (mem?: string | null): number => {
  if (!mem) return 0;
  try {
    const lower = mem.trim().toLowerCase();
    if (lower.endsWith('gi')) {
      return Number(lower.replace(/gi/, '')) * 1024;
    }
    if (lower.endsWith('g')) {
      return Number(lower.replace(/g/, '')) * 1024;
    }
    if (lower.endsWith('mi')) {
      return Number(lower.replace(/mi/, ''));
    }
    if (lower.endsWith('m')) {
      return Number(lower.replace(/m/, ''));
    }
    return Number(lower);
  } catch {
    return 0;
  }
};

/**
 * Simple wildcard match where pattern may contain a single trailing '*'.
 */
const wildcardMatch = (rawPattern?: unknown, rawValue?: unknown): boolean => {
  const pattern = typeof rawPattern === 'string' ? rawPattern : '';
  const value = typeof rawValue === 'string' ? rawValue : '';

  if (!pattern) return false;
  if (pattern === '*' || pattern === value) return true;

  const starIdx = pattern.indexOf('*');
  if (starIdx === -1) return pattern === value;

  const prefix = pattern.slice(0, starIdx);
  return value.startsWith(prefix);
};

export const computeModelNodepoolCompatibility = (
  modelInfo?: CF.API.Models.InferenceComputeInfo,
  nodeInfo?: CF.API.Compute.InstanceType['compute_info'],
): CompatibilityResult => {
  // Validate presence of meaningful data
  const isEmptyObj = (obj: unknown): boolean => !!obj && Object.keys(obj as Record<string, unknown>).length === 0;

  if (!modelInfo || isEmptyObj(modelInfo)) {
    return {
      compatible: false,
      reasons: [
        'Only new compute orchestration models can be deployed and served with Compute Orchestration. This current model is a legacy model type that is not supported. Try uploading a similar model using compute orchestration instead of this model if you want to schedule it to your own compute!',
      ],
    };
  }

  if (!nodeInfo || isEmptyObj(nodeInfo)) {
    return { compatible: false, reasons: ['Nodepool instance information unavailable'] };
  }

  const reasons: string[] = [];

  // NOTE: For now CPU validation in the UI is not required.
  // // CPU cores
  // const modelCores = parseCpuCores(modelInfo.cpu_limit);
  // const nodeCores = parseCpuCores(nodeInfo.cpu_limit);
  // if (modelCores > 0 && nodeCores < modelCores) {
  //   reasons.push(`Model requires at least ${modelCores} CPU cores, nodepool has ${nodeCores}.`);
  // }

  // Accelerator type
  const modelAccPatterns: string[] = Array.isArray(modelInfo.accelerator_type)
    ? (modelInfo.accelerator_type as string[])
    : modelInfo.accelerator_type
    ? [String(modelInfo.accelerator_type)]
    : [];

  const nodeAccRaw = nodeInfo.accelerator_type;
  const nodeAccType = Array.isArray(nodeAccRaw) ? nodeAccRaw[0] : nodeAccRaw;

  if (modelAccPatterns.length) {
    const typeMatch = modelAccPatterns.some((pattern) => wildcardMatch(pattern, nodeAccType));
    if (!typeMatch) {
      reasons.push(
        `Accelerator ${typeof nodeAccType === 'string' ? nodeAccType : ''} incompatible with model requirement (${modelAccPatterns.join(', ')})`,
      );
    }
  }

  // GPU memory
  const modelNumAcc = modelInfo.num_accelerators || 0;
  const nodeNumAcc = nodeInfo.num_accelerators || 0;
  const modelMemPerAcc = parseMemoryMi(modelInfo.accelerator_memory);
  const nodeMemPerAcc = parseMemoryMi(nodeInfo.accelerator_memory);

  const modelTotalMem = modelMemPerAcc * (modelNumAcc || 1);
  const nodeTotalMem = nodeMemPerAcc * (nodeNumAcc || 1);
  const modelTotalGi = Math.round(modelTotalMem / 1024);
  const nodepoolTotalGi = Math.round(nodeTotalMem / 1024);

  if (modelTotalMem > 0 && nodeTotalMem < modelTotalMem) {
    reasons.push(`Model requires ${modelTotalGi}Gi GPU memory, nodepool provides ${nodepoolTotalGi}Gi.`);
  }

  return reasons.length ? { compatible: false, reasons } : { compatible: true };
};
